import React, { Fragment } from 'react'
import { Formik } from 'formik'
import { validationSchema } from './validationSchema'
import snakeCase from 'lodash/snakeCase'
import map from 'lodash/map'
import without from 'lodash/without'
import startCase from 'lodash/startCase'

const CRUD = ['create', 'read', 'update', 'delete']
const SUBJECTS = {
  Access: ['dashboard', 'admin_section'],
  AiChat: ['read'],
  Alergen: ['read'],
  BoilerBatchSnapshot: ['read'],
  Control: ['read', 'update'],
  Currency: ['read'],
  CustomVision: ['read'],
  ExternalApiWebhookLog: ['read'],
  Fraction: ['read'],
  Group: ['read'],
  Location: CRUD,
  MainBatchSnapshot: ['read'],
  ManuallyScheduledCommand: ['create', 'read'],
  MenuCategory: CRUD,
  Menu: ['create', 'update'],
  Monitor: ['read'],
  Order: ['read', 'create', 'confirm_manual_payment', 'mark_as_delivered', 'cancel_manually_paid_payment'],
  PlateContainer: ['create'],
  ProductCategory: CRUD,
  ProductContainer: ['create', 'delete'],
  Product: CRUD,
  QueuedOrder: ['read'],
  Recipe: CRUD,
  ReplenishmentOrder: ['create', 'update'],
  Role: CRUD,
  SchedulerRunReport: ['read'],
  ServiceApp: ['cleaning', 'replenishment', 'forced_replenishment', 'maintenance', 'forced_actions'],
  Setting: [
    'read', 'update', 'perform_action', 'mark_robot_as_reset',
    'asap_scheduling', 'enable_sms_notifications', 'force_finish_service_session', 'open_locker',
    'clear_mobile_app_tasks'
  ],
  Unit: ['read'],
  User: CRUD,
  Voucher: ['read', 'create', 'invalidate'],
  WebRequestLog: ['read'],
  CommandStepDuration: ['read', 'update'],
  SmsNotification: ['read'],
  ServiceTask: CRUD,
  ServiceTaskExecution: CRUD,
  AuditLog: ['read'],
  SentryAlert: ['read'],
  AlertScenario: CRUD,
  AlertScenarioExecution: ['read'],
  BusinessError: ['read'],
  SensorLog: ['read'],
  RobotSensor: CRUD,
  HeatEnergyReading: ['read'],
}

const RolesForm = (props) => (
  <Formik
    initialValues={{ name: props.name, permissions: props.permissions }}
    validationSchema={validationSchema}
    onSubmit={(values, { setStatus, setSubmitting }) =>
      props.onSubmit(values, (success, errorData) => {
        if (!success) {
          let status = {}
          const fields = Object.getOwnPropertyNames(errorData.full_errors)
          for (let key in fields) {
            status[`${fields[key]}_error`] = errorData.full_errors[fields[key]].join(', ')
          }
          setStatus(status)
        }
        setSubmitting(false)
      })
    }
  >
    {({
      values,
      errors,
      touched,
      status,
      handleChange,
      handleBlur,
      handleSubmit,
      isSubmitting,
      setStatus,
      setFieldValue,
    }) => {
      const enhancedHandleChange = (e) => {
        let newStatus = Object.assign({}, status) // could be stale?
        newStatus[`${e.target.name}_error`] = ""
        setStatus(newStatus)
        handleChange(e)
      }

      const renderField = (label, name, type, placeHolder, autoComplete = 'off') => {
        const errorsPresent = errors[name] && touched[name] || status && status[`${name}_error`]
        return (
          <div className="form-group">
            <label>{label}</label>
            <input
              id={name}
              name={name}
              type={type}
              onBlur={handleBlur}
              value={values[name]}
              placeholder={placeHolder}
              autoComplete={autoComplete}
              onChange={enhancedHandleChange}
              className={`form-control inverted ${errorsPresent && 'is-invalid'}`}
            />
            {errorsPresent && <div className="invalid-feedback">{errors[name] || status && status[`${name}_error`]}</div>}
          </div>
        )
      }

      const renderSubject = (label, name, actions) => {
        const allChecked = values.permissions.filter(permission => permission.subject === name).length === actions.length

        return (
          <div className='row mb-3'>
            <div className='col-3'>
              {label}
            </div>
            <div className='col-9 d-flex flex-wrap'>
              {actions.map(action => {
                const permission = values.permissions.find(permission => permission.subject === name && permission.action === action)

                return (
                  <fieldset key={action} className="form-group boolean mb-0 me-3">
                    <div className="form-check">
                      <input
                        id={`${name}-${action}`}
                        type="checkbox"
                        className="form-check-input boolean"
                        checked={!!permission}
                        onChange={() =>
                          setFieldValue('permissions', !!permission ? without(values.permissions, permission) : [...values.permissions, { subject: name, action }])
                        }
                      />
                      <label className="form-check-label boolean" htmlFor={`${name}-${action}`}>
                        {startCase(action)}
                      </label>
                    </div>
                  </fieldset>
                )
              })}
              {actions.length > 2 && (
                <fieldset className="form-group boolean mb-0">
                  <div className="form-check">
                    <input
                      id={`${name}-all`}
                      type="checkbox"
                      className="form-check-input boolean"
                      checked={allChecked}
                      onChange={() => {
                        const valuesWithout = values.permissions.filter(permission => permission.subject !== name)

                        if (allChecked) {
                          setFieldValue('permissions', valuesWithout)
                        } else {
                          setFieldValue('permissions', [...valuesWithout, ...actions.map(action => ({ subject: name, action }))])
                        }
                      }}
                    />
                    <label className="form-check-label boolean" htmlFor={`${name}-all`}>
                      Check All
                    </label>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        )
      }

      return (
        <form autoComplete="off" onSubmit={handleSubmit}>
          <div className='row mb-3'>
            <div className='col'>
              <p className='form-section-title'>Name</p>
              {renderField('Name', 'name', 'name', 'Enter name')}
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col'>
              <p className='form-section-title'>Permissions</p>
              {map(SUBJECTS, (permissions, subject) => (
                <Fragment key={subject}>
                  {renderSubject(subject, snakeCase(subject), permissions)}
                </Fragment>
              ))}
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>Submit</button>
          <button className="btn btn-link" onClick={() => window.history.back()}>Back to list</button>
        </form>
      )
    }}
  </Formik>
)

export default RolesForm
